import { useCallback, useMemo, useState } from 'react';
import { MergeDownloadFile } from '../../types';

export const MIN_MERGE_DOCUMENTS = 2

export type MergeSelectionItem = {
  /** Unique key for the document within the Documents section. */
  key: string;
  /** Request body fragment the backend needs to locate the document. */
  file: MergeDownloadFile;
  /** Human readable name, used for accessibility labels and tracking. */
  displayName: string;
}

/**
 * Tracks which PDF documents a user has picked for merging. Selection order is
 * preserved: items are appended when selected and removed when deselected, so
 * the resulting list is the order the merged PDF should use.
 */
export function useDocumentMergeSelection() {
  const [isSelecting, setIsSelecting] = useState(false)
  const [selected, setSelected] = useState<MergeSelectionItem[]>([])

  const start = useCallback(() => {
    setIsSelecting(true)
  }, [])

  const cancel = useCallback(() => {
    setIsSelecting(false)
    setSelected([])
  }, [])

  const toggle = useCallback((item: MergeSelectionItem) => {
    setSelected(prev => {
      const exists = prev.some(s => s.key === item.key)
      if (exists) {
        return prev.filter(s => s.key !== item.key)
      }
      return [...prev, item]
    })
  }, [])

  const indexByKey = useMemo(() => {
    const map: Record<string, number> = {}
    selected.forEach((item, i) => {
      map[item.key] = i + 1
    })
    return map
  }, [selected])

  const isSelected = useCallback((key: string) => key in indexByKey, [indexByKey])

  /** 1-based position of the document in the merge order, or undefined. */
  const selectionIndex = useCallback((key: string): number | undefined => indexByKey[key], [indexByKey])

  return {
    isSelecting,
    selected,
    count: selected.length,
    canMerge: selected.length >= MIN_MERGE_DOCUMENTS,
    start,
    cancel,
    toggle,
    isSelected,
    selectionIndex,
  }
}
